/**
 * RadioSet component module.
 * @module shared/components/RadioSet
 *
 */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {fill, layoutAttribute} from './cssUtils';

import RadioOption from './RadioOption';
import Column from './Column';
import Row from './Row';
import TextInput from './TextInput';

const _RadioSet = styled.div`
  ${(props) => fill(props.width)}
  margin: 5px;
  ${layoutAttribute.css};
`;

const _Label = styled.label`
  font-family: ${(props) => props.theme.labelText.name};
  color: ${(props) => props.theme.dark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)'};
  text-align: left;
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

/**
 * Radio Set component
 * @name RadioSet
 */
const RadioSet = (props) => {
  const components = {
    column: Column,
    row: Row,
  };
  const {
    dataPoint,
    disabled,
    onChange,
    options,
    optionWidth,
    orientation = 'column',
    readOnly,
    title,
    width,
    layout,
  } = props;

  const DisplayType = components[orientation] || Column;
  const orientationAttributes = orientation === 'column' ? {
    height: 'auto',
    // width,
    justifyContent: 'top',
    alignItems: 'left',
    layout: {grow: 1},
    showDividers: false,
  } : {
    height: 'auto',
    justifySubRow: 'top',
    // width,
    wrap: true,
    alignItems: 'top',
    alignSubRow: 'top',
    showDividers: false,
  };

  const optionsList = <>{
    readOnly ? <TextInput title={title}
                          type='text'
                          placeholder='No Selection'
                          dataPoint={{
                            name: dataPoint.name,
                            value: (options.find(option => option.value === dataPoint.value) || {}).label,
                          }}
                          width={width}
                          layout={layout}
                          readOnly={true}/>
      : <>{options.map(option => <RadioOption key={`${option.label}-${dataPoint.name}`}
                                              dataPoint={{
                                                name: dataPoint.name,
                                                value: option.value,
                                              }}
                                              name={dataPoint.name}
                                              selected={dataPoint.value === option.value}
                                              onChange={onChange}
                                              disabled={disabled}
                                              label={option.label}
                                              width={orientation === 'column' ? width : optionWidth}/>)}
      </>
  }</>;

  return <_RadioSet width={width} layout={layout}>
    {!readOnly && title && <_Label disabled={disabled}>{title}:</_Label>}
    <DisplayType {...orientationAttributes}>
      {optionsList}
    </DisplayType>
  </_RadioSet>;
};

export default RadioSet;

RadioSet.propTypes = {
  /**
   * The attribute being set
   */
  dataPoint: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
  }).isRequired,
  /**
   * If true, then Radio Button is disabled
   */
  disabled: PropTypes.bool,
  /**
   * The list of radio options to display
   */
  options: (props, propName, componentName) => {
    PropTypes.checkPropTypes({
      [propName]: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.bool,
      })),
    }, props, propName, componentName);
    const defaultSelection = props.options.filter(option => !!props.dataPoint.value === !!option.value);
    if (defaultSelection.length > 1 || !defaultSelection.length) {
      return new Error(`${componentName} requires a default selection.`);
    }
    return null;
  },
  /**
   * The Radio Set orientation for display
   */
  orientation: PropTypes.oneOf(['column', 'row']),
  /**
   * The on change function (name, value)
   */
  onChange: PropTypes.func,
  /**
   * If true, then displays the default selection as a Text Input
   */
  readOnly: PropTypes.bool,
  /**
   * The title label of the Radio Set
   */
  title: PropTypes.string,
  /**
   * The width of the Radio Set
   */
  width: PropTypes.string,
  /**
   * The width of the radio options. If orientation is row, then optionWidth is required.
   */
  optionWidth: (props, propName, componentName) => {
    PropTypes.checkPropTypes({[propName]: PropTypes.string}, props, propName, componentName);
    if (props.orientation === 'row' && !props[propName]) {
      return new Error(`If ${componentName}'s orientation is 'row', then ${propName} is required.`);
    }
    return null;
  },
  layout: layoutAttribute.propTypes,
};
